import React, { useEffect, useState, useRef } from 'react';
import { isSupported } from 'firebase/messaging';
import useProgressiveWebApp from '@src/shared/hooks/use-pwa';
import to from 'await-to-js';
import { usePermissionConsentState } from '@src/shared/context/permission-consent';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import FirebaseAdapter from './FirebaseAdapterClass';
import PushNotificationSubscriptionApiService from '@shared/services/push-notification-subscription-api';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '@src/shared/hooks/use-online-status';
const FirebaseConnector = () => {
  const isDebuggerActive = !!localStorage.getItem('FB_DEBUGGER');
  const [fbToken, setfbToken] = useState<null | string>(null);
  const [isFBSupported, setIsFBSupported] = useState<null | boolean>(false);
  const { isAppInstalled } = useProgressiveWebApp();
  const { grantType } = usePermissionConsentState();
  const [pushNotificationInitialized, setPushNotificationInitialized] = useState(false);
  const { keycloakToken, restaurant } = useKeycloakUserState();
  const FirebaseAdapterInstance = useRef<null | FirebaseAdapter>(null);
  const { i18n } = useTranslation();
  const { isBrowserOnline } = useOnlineStatus();

  const subscribeToSubscriptionAPI = (firebaseToken: string) => {
    const languageCode = i18n.language || 'en';
    if (!restaurant?.id) {
      console.warn('Restaurant ID is not available for push notification subscription');
      return;
    }

    PushNotificationSubscriptionApiService.subscribe(restaurant?.id, firebaseToken, languageCode);
  };

  const initalizePushNotificationStructure = async () => {
    const {
      apiKey,
      authDomain,
      databaseURL,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
      vapID,
    } = FIREBASE_SDK_VARIABLES;

    if (!FirebaseAdapterInstance.current) {
      FirebaseAdapterInstance.current = new FirebaseAdapter({
        apiKey,
        authDomain,
        databaseURL,
        projectId,
        storageBucket,
        messagingSenderId,
        appId,
        measurementId,
        vapID,
      });
    }

    const FBAdapter: FirebaseAdapter = FirebaseAdapterInstance.current;

    if (isDebuggerActive) {
      FBAdapter.activateDebugger();
    }

    await to(FBAdapter.installServiceWorker());
    await to(FBAdapter.subscribeToPushManager());

    const support = await isSupported();

    setIsFBSupported(!!support);

    if (!support) {
      FBAdapter.DEBUGGER_MESSAGE('FIREBASE IS NOT SUPPORTED');
      return;
    }

    FBAdapter.initializeFireBaseApp();

    const [firebaseTokenError, firebaseToken] = await to(FBAdapter.getFirebaseToken());

    if (firebaseToken && restaurant?.id) {
      setfbToken(firebaseToken);
      subscribeToSubscriptionAPI(firebaseToken);
    } else {
      FBAdapter.DEBUGGER_MESSAGE(
        'FIREBASE TOKEN NOT SET Restaurant ID:',
        restaurant?.id,
        ' firebaseToken:',
        firebaseToken
      );
    }
    if (firebaseTokenError) {
      console.log(`Error on getFirebaseToken: `, firebaseTokenError);
      return;
    }

    setPushNotificationInitialized(true);
  };

  useEffect(() => {
    console.log('ONLINE STATUS CHANGED', isBrowserOnline);
    if (
      isAppInstalled &&
      keycloakToken &&
      grantType === 'granted' &&
      !pushNotificationInitialized &&
      isBrowserOnline &&
      restaurant?.id
    ) {
      console.log('PUSH NOTIFICATION INITIALIZE TRIGGERED');
      initalizePushNotificationStructure();
    }
  }, [grantType, keycloakToken, isAppInstalled, isBrowserOnline, restaurant?.id]);

  useEffect(() => {
    if (fbToken && restaurant?.id) {
      subscribeToSubscriptionAPI(fbToken);
    }
  }, [i18n.language, restaurant?.id, fbToken]);

  return (
    (isDebuggerActive && (
      <div style={{ width: '100vw', wordBreak: 'break-word', height: 'auto' }}>
        [Firebase Debugger ({JSON.stringify(pushNotificationInitialized)})] [Is FB supported :{' '}
        {JSON.stringify(isFBSupported)}] <i>Firebase Token</i> ={' '}
        <b onClick={() => console.log(fbToken)}>{fbToken}</b>
        <br />
        [Is App Installed : {isAppInstalled.toString()}] [Grant Type: {grantType.toString()}]
      </div>
    )) || <></>
  );
};

export default FirebaseConnector;
